import { nanoid } from 'nanoid';
import { getUserTier, TIER_ANON } from './userTier';

/**
 * Identity utilities for the single-identity model.
 *
 * Every user (Profile or Anon) has a persistent ID stored in localStorage:
 * - Profile users: profile_user_id (nanoid(6), e.g., "V1StGX")
 * - Anon users: anon_user_id (anon_${nanoid(8)}, e.g., "anon_V1StGXRM")
 *
 * These IDs are used as senderId in all messages for consistent
 * sent/received display logic across all connection types.
 */

const ANON_USER_ID_KEY = 'anon_user_id';
const PROFILE_USER_ID_KEY = 'profile_user_id';

/**
 * Gets or creates a persistent anonymous user ID.
 *
 * On first call (first QR scan), generates a new ID and stores it.
 * On subsequent calls, returns the existing ID.
 *
 * @returns {string} The anon_user_id (format: anon_XXXXXXXX)
 */
export const getOrCreateAnonUserId = () => {
  let anonUserId = localStorage.getItem(ANON_USER_ID_KEY);

  if (!anonUserId) {
    anonUserId = `anon_${nanoid(8)}`;
    localStorage.setItem(ANON_USER_ID_KEY, anonUserId);
    console.log('[IDENTITY] Created new anon_user_id:', anonUserId);
  } else {
    console.log('[IDENTITY] Using existing anon_user_id:', anonUserId);
  }

  return anonUserId;
};

/**
 * Gets the current user's sender ID for messages.
 *
 * Returns the appropriate ID based on user type:
 * - Profile users: profile_user_id
 * - Anon users: anon_user_id (read only — never minted here)
 *
 * @returns {string|null} The sender ID, or null if no identity exists
 */
export const getSenderId = () => {
  // Minted or Full: the profile id IS the identity, whatever the mode flag
  // says — the boot path can re-derive user_type='anon' from an Anon entry
  // URL after mint, and every consumer (sidedness, previews, unread, message
  // stamping) must keep answering consistently (f/chat-sidedness; auth.md
  // § identity signal rules user_type out — "the 3b bug vector"). On THIS
  // branch the tier rail (both profile keys required) keeps a stray early
  // profile_user_id from hijacking an un-minted Anon; the legacy user_type
  // ladder below retains its historical behavior — full collapse onto the
  // tier rail is logged Future Work (needs the Full-conversion window audit).
  if (getUserTier() !== TIER_ANON) {
    return localStorage.getItem(PROFILE_USER_ID_KEY);
  }

  const userType = sessionStorage.getItem('user_type');

  if (userType === 'profile') {
    return localStorage.getItem(PROFILE_USER_ID_KEY);
  }

  // No lazy mint (f/identity-ownership): a guest id is created and claimed
  // once, at the scan (ensureAnonIdentity), never as a side effect of a read.
  if (userType === 'anon') {
    return localStorage.getItem(ANON_USER_ID_KEY);
  }

  // Fallback: check both (for edge cases during transition)
  return localStorage.getItem(PROFILE_USER_ID_KEY) || localStorage.getItem(ANON_USER_ID_KEY);
};

/**
 * Gets the anon_user_id if it exists (does not create one).
 *
 * @returns {string|null} The anon_user_id or null if not set
 */
export const getAnonUserId = () => {
  return localStorage.getItem(ANON_USER_ID_KEY);
};

const IDENTITY_ALIASES_KEY = 'identity_aliases';

// Raw-string-keyed caches: storage is re-read every call (nothing can go
// stale), only the JSON parse and Set rebuild are skipped when unchanged —
// these sit on the per-message hot path via canonicalizeSenderId/isOwnSenderId.
let aliasesRawCache;
let aliasesParsed = [];
const readAliases = () => {
  const raw = localStorage.getItem(IDENTITY_ALIASES_KEY);
  if (raw !== aliasesRawCache) {
    aliasesRawCache = raw;
    try {
      aliasesParsed = JSON.parse(raw) || [];
    } catch {
      aliasesParsed = [];
    }
  }
  return aliasesParsed;
};

/**
 * Append-only local record of every id this person has held on this device.
 * Written at Simple mint and before clearAnonIdentity removes the anon key —
 * pre-conversion message history stays attributable after either transition
 * (f/chat-sidedness; the server-side twin is the RLS-locked anon_aliases
 * ledger, which the client never joins — db-epic ledger rule).
 */
export const recordIdentityAlias = (id) => {
  if (!id) return;
  const aliases = readAliases();
  if (!aliases.includes(id)) {
    localStorage.setItem(IDENTITY_ALIASES_KEY, JSON.stringify([...aliases, id]));
  }
};

/**
 * Every id this person has held on this device: current keys + recorded
 * aliases. Sidedness checks membership here, never a single current id —
 * the DB may hold rows keyed to any of them (pre-mint history, un-rekeyed
 * rows, or post-rekey rows read while the client holds the old identity).
 *
 * @returns {Set<string>}
 */
let ownSetCache = null;
let ownSetKey = '';
export const getOwnIdentitySet = () => {
  const profileId = localStorage.getItem(PROFILE_USER_ID_KEY);
  const anonId = localStorage.getItem(ANON_USER_ID_KEY);
  const aliasesRaw = localStorage.getItem(IDENTITY_ALIASES_KEY);
  const key = `${profileId}|${anonId}|${aliasesRaw}`;
  if (!ownSetCache || key !== ownSetKey) {
    ownSetKey = key;
    ownSetCache = new Set([profileId, anonId, ...readAliases()].filter(Boolean));
  }
  return ownSetCache;
};

/**
 * Sidedness predicate: was this senderId ever this device's own identity?
 * Bare membership, no tier gate — every id in the set was only ever used by
 * this person, so classification is safe for Anon and minted alike. This is
 * the ONE owned predicate consumers use (never hand-composed comparisons —
 * the userTier doctrine); it also covers messages arriving via the
 * cache-first instant render, which never pass deserializeMessage.
 */
export const isOwnSenderId = (id) => !!id && getOwnIdentitySet().has(id);

/**
 * Ingress normalization: rewrite any own historical senderId to the stable
 * profile id. Tier-GATED, unlike isOwnSenderId — an un-minted Anon's rows
 * must never be re-attributed (a stray early profile_user_id from visiting
 * ProfileScreen would hijack them; tier requires both profile keys).
 */
export const canonicalizeSenderId = (id) => {
  if (getUserTier() === TIER_ANON) return id;
  return isOwnSenderId(id) ? localStorage.getItem(PROFILE_USER_ID_KEY) : id;
};

/**
 * Replaces this browser's guest id with a fresh one (f/identity-ownership):
 * used when the server register says the current id isn't ours to use
 * (taken / locked / invalid). The old id is recorded as an alias FIRST, so its
 * messages stay on this side (sidedness rails). Unlike clearAnonIdentity, the
 * conversion marker is untouched — this is not a conversion.
 *
 * @returns {string} The new anon_user_id
 */
export const rotateAnonUserId = () => {
  recordIdentityAlias(localStorage.getItem(ANON_USER_ID_KEY));
  localStorage.removeItem(ANON_USER_ID_KEY);
  return getOrCreateAnonUserId();
};

/**
 * Drops the leftover anon identity once a profile login is established.
 *
 * On Android the installed PWA shares localStorage with the browser, so a lingering
 * anon_user_id makes the session-handoff relay (useConversionCheck) mis-fire after
 * conversion — stranding the user as Anon, or full-wiping the shared session. Removing the
 * anon id + the handoff marker (converted_profile_id) here keeps the relay asleep.
 *
 * Selective removal only — NEVER localStorage.clear(), which would erase the freshly
 * written profile login. No-op on iOS (the isolated PWA has neither key).
 * The anon id is recorded as an alias FIRST — deleting it unrecorded would make
 * pre-conversion history unattributable (f/chat-sidedness).
 */
export const clearAnonIdentity = () => {
  recordIdentityAlias(localStorage.getItem(ANON_USER_ID_KEY));
  localStorage.removeItem(ANON_USER_ID_KEY);
  localStorage.removeItem('converted_profile_id');
};

/**
 * Valid profile_user_id format: 4-12 alphanumeric chars, hyphens, underscores.
 * Default generator produces 6-char nanoid. Range accommodates custom IDs
 * set via the Edit Profile ID modal (dev tool for QR test images).
 */
const PROFILE_ID_REGEX = /^[a-zA-Z0-9_-]{4,12}$/;

/**
 * Gets or creates a valid profile_user_id.
 *
 * Handles three cases:
 * - Missing: generates nanoid(6), stores it
 * - Valid: returns as-is
 * - Malformed (e.g., anon_XXXXXXXX from broken carry-forward): regenerates
 *
 * @returns {string} A valid profile_user_id
 */
export const getOrCreateProfileUserId = ({ persist = true } = {}) => {
  const existing = localStorage.getItem(PROFILE_USER_ID_KEY);

  if (existing && PROFILE_ID_REGEX.test(existing)) {
    return existing;
  }

  if (existing) {
    console.warn('[IDENTITY] Regenerating malformed profile_user_id:', existing);
  }

  const newId = nanoid(6);
  // persist: false — the caller stores it (storeProfileUserId) once the server
  // row exists; until then every event would name a profile the server
  // doesn't know and be refused (open-access lockdown)
  if (persist) {
    localStorage.setItem(PROFILE_USER_ID_KEY, newId);
    console.log('[IDENTITY] Created new profile_user_id:', newId);
  }
  return newId;
};

/** Store the profile id once its server row exists (pairs with persist: false). */
export const storeProfileUserId = (profileUserId) => {
  localStorage.setItem(PROFILE_USER_ID_KEY, profileUserId);
};

/**
 * Validates a profile_user_id format without creating one.
 *
 * @param {string} id - The ID to validate
 * @returns {boolean} True if valid format
 */
export const isValidProfileUserId = (id) => {
  return PROFILE_ID_REGEX.test(id);
};

/**
 * Gets the profile_user_id if it exists (does not create one).
 *
 * @returns {string|null} The profile_user_id or null if not set
 */
export const getProfileUserId = () => {
  return localStorage.getItem(PROFILE_USER_ID_KEY);
};
